import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Popup from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import TextField from '@atlaskit/textfield';

import { type ProductKeys } from '../../../../constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../../constants/schemas/3p-config';
import type { TriggerComponentProps } from '../../../../types';
import { isProductSmartlink } from '../../../../utils/connectors';
import type { OAuthSource } from '../../../../utils/events/oauth-events';
import { getUnifiedSearchRightRailEnhancementsExpValue } from '../../../../utils/experiments';
import {
	OAuthWarningWrapper,
	type UseIsUserOAuthed,
	type UseOAuthHandlers,
} from '../../../oauth-warning-wrapper';
import FilterOption from '../filter-option';

import { messages } from './messages';

const TRIGGER_HEIGHT_OFFSET = 24;

export interface FilterOption {
	label: React.ReactNode;
	count?: number;
	value: ProductKeys | undefined;
	isSelected: boolean;
	onSelect: () => void;
	hideFilter: () => void;
	showFilter: () => void;
	compositeKey?: string;
	datasourceId?: string;
}

type ShowMoreAppsDialogParams = {
	options: FilterOption[];
	useIsUserOAuthed: UseIsUserOAuthed;
	useOAuthHandlers: UseOAuthHandlers;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	oAuthSource: OAuthSource;
};

const styles = cssMap({
	showMoreApps: {
		/**
		 * Show more apps popup max height calculation:
		 * 	top padding = 8px
		 * 	text field height = 40px
		 * 	text field bottom padding = 12px
		 * 	max visible options = 8
		 * 	option height = 38px
		 * total = 8px + 40px + 12px + (8 * 38px) = 364px
		 */
		maxHeight: '364px',
		overflowY: 'auto',
	},
});

const ShowMoreAppsDialog = ({
	options = [],
	useIsUserOAuthed,
	useOAuthHandlers,
	thirdPartyConfigs,
	oAuthSource,
}: ShowMoreAppsDialogParams): React.JSX.Element | null => {
	const [isOpen, setIsOpen] = useState(false);
	const [filter, setFilter] = useState<RegExp>();
	const { formatMessage } = useIntl();
	if (!options.length) {
		return null;
	}

	return (
		<Popup
			content={() => (
				<Box paddingBlock="space.100" xcss={styles.showMoreApps}>
					<Box paddingInline="space.100" paddingBlockEnd="space.150">
						<TextField
							placeholder={formatMessage(messages.filterPlaceholder)}
							onChange={(ev) => setFilter(new RegExp(ev.currentTarget.value, 'i'))}
						/>
					</Box>
					<Box role="list">
						{options
							.filter(
								(option) =>
									!filter || (typeof option.label === 'string' && filter.test(option.label)),
							)
							.map((option) => {
								const isSmartlink = isProductSmartlink({
									productKey: option.value,
									thirdPartyConfigs,
								});

								const reactKey = option.compositeKey || option.value;

								return (
									<OAuthWarningWrapper
										datasourceId={option.datasourceId}
										key={reactKey}
										product={option.value as ProductKeys}
										trigger={({ oAuthWarning, triggerProps }: TriggerComponentProps) => (
											<FilterOption
												oAuthWarning={oAuthWarning}
												triggerProps={triggerProps}
												key={reactKey}
												isHidden={true}
												option={option}
												onClick={() => {
													if (!oAuthWarning) {
														option.showFilter();
														option.onSelect();
														setIsOpen(false);
													}
												}}
												ariaRole="listitem"
											/>
										)}
										useOAuthHandlers={useOAuthHandlers}
										useIsUserOAuthed={useIsUserOAuthed}
										option={option}
										isSmartlink={isSmartlink}
										oAuthSource={oAuthSource}
										thirdPartyConfigs={thirdPartyConfigs}
										postAuthAction={
											getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
												? () => {
														setIsOpen(false);
													}
												: undefined
										}
										skipFlagsProvider={
											getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
										}
									/>
								);
							})}
					</Box>
				</Box>
			)}
			isOpen={isOpen}
			onClose={() => {
				setIsOpen(false);
				setFilter(undefined);
			}}
			placement="bottom-start"
			shouldFitContainer
			offset={[0, -TRIGGER_HEIGHT_OFFSET]}
			trigger={(triggerProps) => (
				<FilterOption
					oAuthWarning={null}
					triggerProps={triggerProps}
					isDisabled={false}
					isShowMore={true}
					option={{
						label: formatMessage(
							getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
								? messages.showAllAppsLabel
								: messages.showMoreLabel,
						),
						isSelected: false,
						value: undefined,
					}}
					onClick={() => {
						setIsOpen(true);
						setFilter(undefined);
					}}
				/>
			)}
			modifiers={[
				{
					name: 'computeStyles',
					options: {
						// Prevent use of 3D transforms to avoid creating a new stacking context for fixed position children
						// This allows the connection prompt popup to appear to the left of this dialog
						gpuAcceleration: false,
					},
				},
			]}
		/>
	);
};

export default ShowMoreAppsDialog;
